import type {
  ChangeEvent,
  FormEvent,
  ReactElement,
  KeyboardEvent as ReactKeyboardEvent,
  ReactNode,
} from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import classNames from 'classnames';
import type { Components } from 'react-markdown';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import useFileDrop from '../../hooks/useFileDrop';
import styles from './ChatWidget.module.css';
import type {
  ChatAttachmentsController,
  ChatProposalStatus,
  ChatTransport,
  ChatWidgetMessage,
} from './types';
import { ChatTransportError } from './types';

const REMARK_PLUGINS = [remarkGfm];

// Enter sends; the platform's command key + Enter inserts a line break.
const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/.test(navigator.userAgent);
const NEWLINE_MODIFIER_LABEL = IS_MAC ? '⌘' : 'Ctrl';

/** Read the persisted open/closed state for this surface (localStorage-safe). */
function readStoredOpen(storageKey?: string): boolean {
  if (!storageKey || typeof localStorage === 'undefined') return false;
  try {
    return localStorage.getItem(`${storageKey}:open`) === 'true';
  } catch {
    return false;
  }
}

/** Status line shown on a proposal once it's no longer awaiting a decision. */
const PROPOSAL_STATUS_LABEL: Record<ChatProposalStatus, string> = {
  pending: 'Waiting for your confirmation',
  working: 'Working…',
  confirmed: 'Confirmed',
  declined: 'Declined',
  failed: 'Could not be completed — try again',
};

export interface ChatWidgetProps {
  transport: ChatTransport;
  title: string;
  subtitle?: string;
  /** Shown as tappable prompts while the thread is empty. */
  suggestions?: string[];
  inputPlaceholder?: string;
  /**
   * Prefixed to citation URLs that are site-relative (e.g. '/docs/…'). Citation
   * URLs that already carry an origin are used as-is.
   */
  citationBaseUrl?: string;
  /** Turn cap mirroring the backend limit; input locks when reached. */
  maxMessages?: number;
  /** Launcher glyph. Defaults to a chat bubble; the dashboard passes the Revvly mark. */
  launcherIcon?: ReactNode;
  /** When set, the open/closed state is persisted under this localStorage key. */
  storageKey?: string;
  /** Seed the thread with a previously-loaded conversation (async-safe). */
  initialMessages?: ChatWidgetMessage[];
  /**
   * Client-side navigation for internal (relative) links in assistant replies.
   * When provided, an internal link navigates in-app without a full reload —
   * so the widget stays mounted (open, same conversation). External links and
   * modifier-clicks always fall back to the browser's default.
   */
  onNavigate?: (href: string) => void;
  /**
   * File attachments, owned by the host (it does the uploading). When set, the
   * composer gets an attach button and a tray, and files can be dropped
   * anywhere on the open panel. Omit it for surfaces with no uploads.
   */
  attachments?: ChatAttachmentsController;
}

const DEFAULT_MAX_MESSAGES = 12;

/** Absolute ('https://…') or protocol-relative ('//…') — an origin is present. */
const HAS_ORIGIN = /^([a-z][a-z0-9+.-]*:)?\/\//i;

/**
 * Citation href. The backend now returns absolute documentation URLs, but a
 * relative one is still valid (older replies, other corpora), so the base is
 * applied only when the URL has no origin of its own — concatenating onto an
 * absolute URL would produce 'https://host/https://host/docs/…'.
 */
function citationHref(baseUrl: string, url: string): string {
  return HAS_ORIGIN.test(url) ? url : `${baseUrl}${url}`;
}

/**
 * Intercom-style floating support chat: a launcher pinned to the corner
 * and a compact conversation panel. Purely presentational + local state;
 * the backend is injected via ChatTransport, so the same component serves
 * the anonymous website (docs bot) and the authenticated dashboard
 * (agent) later.
 *
 * Styling reads canonical --rv-* tokens only, so the host surface decides
 * the skin (light panel over the dark marketing canvas today; dashboard
 * light/dark modes for free).
 */
export function ChatWidget({
  transport,
  title,
  subtitle,
  suggestions = [],
  inputPlaceholder = 'Ask a question',
  citationBaseUrl = '',
  maxMessages = DEFAULT_MAX_MESSAGES,
  launcherIcon,
  storageKey,
  initialMessages,
  onNavigate,
  attachments,
}: ChatWidgetProps): ReactElement {
  const [isOpen, setIsOpen] = useState(() => readStoredOpen(storageKey));
  const [messages, setMessages] = useState<ChatWidgetMessage[]>(initialMessages ?? []);
  const [draft, setDraft] = useState('');
  const [isSending, setIsSending] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const threadRef = useRef<HTMLDivElement>(null);
  const seededRef = useRef(false);

  const capReached = messages.filter((message) => !message.isError).length >= maxMessages;

  // react-markdown link renderer. Internal links navigate in-app (no reload →
  // the widget stays open with the same conversation) when onNavigate is given;
  // external links and modifier-clicks fall back to the browser default. `node`
  // is dropped so it never reaches the DOM element.
  const markdownComponents = useMemo<Components>(
    () => ({
      a({ node, href, ...props }) {
        // Exclude protocol-relative URLs ("//evil.com") — the browser resolves
        // those to an external origin, so they must not route through onNavigate.
        const internal =
          typeof href === 'string' && href.startsWith('/') && !href.startsWith('//');
        if (internal && onNavigate) {
          return (
            // react-markdown passes the link text through `props.children`,
            // which the rule can't see behind the spread.
            // eslint-disable-next-line jsx-a11y/anchor-has-content
            <a
              {...props}
              href={href}
              onClick={(event) => {
                if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
                event.preventDefault();
                onNavigate(href);
              }}
            />
          );
        }
        // As above, the link text arrives via `props.children`.
        // eslint-disable-next-line jsx-a11y/anchor-has-content
        return <a {...props} href={href} />;
      },
    }),
    [onNavigate],
  );

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    // Optional-call: jsdom (tests) and very old browsers lack scrollTo.
    threadRef.current?.scrollTo?.({ top: threadRef.current.scrollHeight });
  }, [messages, isSending]);

  // Persist the open/closed state so it survives reloads (e.g. after clicking
  // a same-tab link the agent produced).
  useEffect(() => {
    if (!storageKey || typeof localStorage === 'undefined') return;
    try {
      localStorage.setItem(`${storageKey}:open`, String(isOpen));
    } catch {
      // localStorage may be unavailable (privacy mode) — non-fatal.
    }
  }, [isOpen, storageKey]);

  // Seed a previously-loaded conversation once it arrives (it's fetched async),
  // but never clobber a thread the user has already started this session.
  useEffect(() => {
    if (seededRef.current || !initialMessages || initialMessages.length === 0) return;
    seededRef.current = true;
    setMessages((current) => (current.length === 0 ? initialMessages : current));
  }, [initialMessages]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen]);

  const send = useCallback(
    async (text: string) => {
      const content = text.trim();
      // A message waits for its uploads, and a failed one must be removed
      // first — otherwise it would silently go out without the file.
      if (!content || isSending || capReached || attachments?.blocked) return;

      // Captured before the tray is cleared below.
      const sent = attachments?.ready ?? [];
      const userMessage: ChatWidgetMessage = {
        role: 'user',
        content,
        ...(sent.length > 0 && { attachments: sent.map(({ name }) => ({ name })) }),
      };
      const thread = [...messages.filter((message) => !message.isError), userMessage];
      setMessages((current) => [...current, userMessage]);
      setDraft('');
      attachments?.clear();
      setIsSending(true);
      try {
        const history = thread.map(({ role, content: turnContent }) => ({
          role,
          content: turnContent,
        }));
        // Options only when there is something to pass, so a host without
        // attachments (the website) sees exactly the call it always has.
        const reply = await (sent.length > 0
          ? transport.send(history, { attachments: sent })
          : transport.send(history));
        setMessages((current) => [
          ...current,
          {
            role: 'assistant',
            content: reply.reply,
            citations: reply.citations ?? [],
            proposals: reply.proposals ?? [],
          },
        ]);
      } catch (error) {
        const message =
          error instanceof ChatTransportError
            ? error.message
            : 'Something went wrong on our side. Please try again.';
        setMessages((current) => [
          ...current,
          { role: 'assistant', content: message, isError: true },
        ]);
      } finally {
        setIsSending(false);
        inputRef.current?.focus();
      }
    },
    [attachments, capReached, isSending, messages, transport],
  );

  const addFiles = useCallback((files: File[]) => attachments?.addFiles(files), [attachments]);
  const { isDragging, bindings: dropBindings } = useFileDrop(
    addFiles,
    Boolean(attachments) && !capReached && !attachments?.full,
  );

  const failedAttachment = attachments?.items.find((item) => item.status === 'error');

  const onPickFiles = (event: ChangeEvent<HTMLInputElement>): void => {
    if (event.target.files?.length) addFiles(Array.from(event.target.files));
    event.target.value = '';
  };

  /** Insert a line break at the caret without sending. */
  const insertNewline = useCallback(() => {
    const el = inputRef.current;
    if (!el) {
      setDraft((current) => `${current}\n`);
      return;
    }
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;
    setDraft((current) => `${current.slice(0, start)}\n${current.slice(end)}`);
    // Restore the caret just after the break, once React re-renders.
    requestAnimationFrame(() => {
      const node = inputRef.current;
      if (node) node.selectionStart = node.selectionEnd = start + 1;
    });
  }, []);

  const onComposerKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key !== 'Enter') return;
      // Cmd/Ctrl+Enter (or Shift+Enter) inserts a line break; plain Enter sends.
      if (event.metaKey || event.ctrlKey) {
        event.preventDefault();
        insertNewline();
        return;
      }
      if (event.shiftKey) return;
      event.preventDefault();
      void send(draft);
    },
    [draft, insertNewline, send],
  );

  /**
   * Confirm or decline a proposed action. Marks the proposal on its
   * originating message, then appends whatever the agent streams back as the
   * continuation (more text and/or further proposals).
   */
  const respondToAction = useCallback(
    async (messageIndex: number, actionId: string, decision: 'confirm' | 'reject') => {
      if (!transport.respondToAction || isSending) return;
      const setProposalStatus = (status: ChatProposalStatus) =>
        setMessages((current) =>
          current.map((message, index) =>
            index === messageIndex
              ? {
                  ...message,
                  proposals: message.proposals?.map((proposal) =>
                    proposal.actionId === actionId ? { ...proposal, status } : proposal,
                  ),
                }
              : message,
          ),
        );

      setIsSending(true);
      setProposalStatus('working');
      try {
        const reply = await transport.respondToAction(actionId, decision);
        setProposalStatus(decision === 'confirm' ? 'confirmed' : 'declined');
        if (reply.reply || (reply.proposals?.length ?? 0) > 0) {
          setMessages((current) => [
            ...current,
            {
              role: 'assistant',
              content: reply.reply,
              citations: reply.citations ?? [],
              proposals: reply.proposals ?? [],
            },
          ]);
        }
      } catch {
        setProposalStatus('failed');
      } finally {
        setIsSending(false);
        inputRef.current?.focus();
      }
    },
    [isSending, transport],
  );

  const onSubmit = (event: FormEvent): void => {
    event.preventDefault();
    void send(draft);
  };

  const reset = (): void => {
    setMessages([]);
    setDraft('');
    attachments?.clear();
    inputRef.current?.focus();
  };

  return (
    <div className={styles.Root}>
      {isOpen && (
        <section
          className={classNames(styles.Panel, { [styles.PanelDragging]: isDragging })}
          role="dialog"
          aria-label={title}
          {...dropBindings}
        >
          {isDragging && (
            <div className={styles.DropOverlay} aria-hidden="true">
              <PaperclipIcon />
              <span>Drop files to attach</span>
            </div>
          )}
          <header className={styles.Header}>
            <div>
              <h2 className={styles.Title}>{title}</h2>
              {subtitle && <p className={styles.Subtitle}>{subtitle}</p>}
            </div>
            <button
              type="button"
              className={styles.IconButton}
              aria-label="Close chat"
              onClick={() => setIsOpen(false)}
            >
              <CloseIcon />
            </button>
          </header>

          <div className={styles.Thread} ref={threadRef} aria-live="polite">
            {messages.length === 0 && (
              <div className={styles.Empty}>
                <p className={styles.EmptyLead}>
                  Ask anything about how Revvly works — answers come from the documentation, with
                  links to read more.
                </p>
                {suggestions.length > 0 && (
                  <div className={styles.Suggestions}>
                    {suggestions.map((suggestion) => (
                      <button
                        key={suggestion}
                        type="button"
                        className={styles.Suggestion}
                        onClick={() => void send(suggestion)}
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {messages.map((message, index) => (
              <div
                key={index}
                className={classNames(styles.Bubble, {
                  [styles.BubbleUser]: message.role === 'user',
                  [styles.BubbleAssistant]: message.role === 'assistant',
                  [styles.BubbleError]: message.isError,
                })}
              >
                {message.role === 'assistant' && !message.isError ? (
                  <div className={styles.BubbleMarkdown}>
                    <Markdown remarkPlugins={REMARK_PLUGINS} components={markdownComponents}>
                      {message.content}
                    </Markdown>
                  </div>
                ) : (
                  <p className={styles.BubbleText}>{message.content}</p>
                )}
                {message.attachments && message.attachments.length > 0 && (
                  <ul className={styles.BubbleAttachments}>
                    {message.attachments.map((file, fileIndex) => (
                      <li key={`${file.name}-${fileIndex}`} className={styles.BubbleAttachment}>
                        <PaperclipIcon />
                        <span className={styles.AttachmentName}>{file.name}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {message.citations && message.citations.length > 0 && (
                  <div className={styles.Citations}>
                    {message.citations.map((citation) => (
                      <a
                        key={citation.url}
                        className={styles.Citation}
                        href={citationHref(citationBaseUrl, citation.url)}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {citation.title}
                      </a>
                    ))}
                  </div>
                )}
                {message.proposals && message.proposals.length > 0 && (
                  <div className={styles.Proposals}>
                    {message.proposals.map((proposal) => (
                      <div key={proposal.actionId} className={styles.Proposal}>
                        <p className={styles.ProposalSummary}>{proposal.summary}</p>
                        {proposal.details && Object.keys(proposal.details).length > 0 && (
                          <dl className={styles.ProposalDetails}>
                            {Object.entries(proposal.details).map(([key, value]) => (
                              <div key={key} className={styles.ProposalRow}>
                                <dt className={styles.ProposalKey}>{key}</dt>
                                <dd className={styles.ProposalValue}>{String(value)}</dd>
                              </div>
                            ))}
                          </dl>
                        )}
                        {proposal.status === 'pending' && transport.respondToAction ? (
                          <div className={styles.ProposalActions}>
                            <button
                              type="button"
                              className={styles.ProposalConfirm}
                              disabled={isSending}
                              onClick={() =>
                                void respondToAction(index, proposal.actionId, 'confirm')
                              }
                            >
                              Confirm
                            </button>
                            <button
                              type="button"
                              className={styles.ProposalDecline}
                              disabled={isSending}
                              onClick={() =>
                                void respondToAction(index, proposal.actionId, 'reject')
                              }
                            >
                              Decline
                            </button>
                          </div>
                        ) : (
                          <p
                            className={classNames(styles.ProposalStatus, {
                              [styles.ProposalStatusError]: proposal.status === 'failed',
                            })}
                          >
                            {PROPOSAL_STATUS_LABEL[proposal.status]}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {isSending && (
              <div className={classNames(styles.Bubble, styles.BubbleAssistant)}>
                <span className={styles.Typing} aria-label="The assistant is typing">
                  <span className={styles.TypingDot} />
                  <span className={styles.TypingDot} />
                  <span className={styles.TypingDot} />
                </span>
              </div>
            )}
          </div>

          {capReached ? (
            <div className={styles.CapNotice}>
              <p className={styles.CapText}>This conversation has reached its length limit.</p>
              <button type="button" className={styles.ResetButton} onClick={reset}>
                Start a new conversation
              </button>
            </div>
          ) : (
            <div className={styles.ComposerArea}>
              {attachments && attachments.items.length > 0 && (
                <ul className={styles.Tray} aria-label="Attached files">
                  {attachments.items.map((item) => (
                    <li
                      key={item.id}
                      className={classNames(styles.Chip, {
                        [styles.ChipError]: item.status === 'error',
                      })}
                      title={item.error ?? item.name}
                    >
                      <span className={styles.AttachmentName}>{item.name}</span>
                      <span className={styles.ChipMeta}>
                        {item.status === 'uploading'
                          ? 'Uploading…'
                          : item.status === 'error'
                            ? 'Failed'
                            : item.sizeLabel}
                      </span>
                      <button
                        type="button"
                        className={styles.ChipRemove}
                        aria-label={`Remove ${item.name}`}
                        onClick={() => attachments.remove(item.id)}
                      >
                        <CloseIcon />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {failedAttachment && (
                <p className={styles.TrayError} role="alert">
                  {failedAttachment.name}: {failedAttachment.error ?? 'Upload failed.'} Remove it
                  to send.
                </p>
              )}
              <form className={styles.Composer} onSubmit={onSubmit}>
                {attachments && (
                  <>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept={attachments.accept}
                      className={styles.FileInput}
                      onChange={onPickFiles}
                      tabIndex={-1}
                      aria-hidden="true"
                    />
                    <button
                      type="button"
                      className={styles.AttachButton}
                      aria-label="Attach a file"
                      disabled={attachments.full}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <PaperclipIcon />
                    </button>
                  </>
                )}
                <textarea
                  ref={inputRef}
                  className={styles.Input}
                  value={draft}
                  rows={1}
                  maxLength={2000}
                  placeholder={inputPlaceholder}
                  aria-label={inputPlaceholder}
                  onChange={(event) => setDraft(event.target.value)}
                  onKeyDown={onComposerKeyDown}
                />
                <button
                  type="submit"
                  className={styles.SendButton}
                  aria-label="Send message"
                  disabled={isSending || !draft.trim() || Boolean(attachments?.blocked)}
                >
                  <SendIcon />
                </button>
              </form>
              <p className={styles.ComposerHint} aria-hidden="true">
                <kbd className={styles.Kbd}>↵</kbd> send
                <span className={styles.HintDot}>·</span>
                <kbd className={styles.Kbd}>{NEWLINE_MODIFIER_LABEL}</kbd>
                <kbd className={styles.Kbd}>↵</kbd> new line
              </p>
            </div>
          )}
        </section>
      )}

      <button
        type="button"
        className={styles.Launcher}
        aria-label={isOpen ? 'Close chat' : 'Open chat'}
        aria-expanded={isOpen}
        onClick={() => setIsOpen((open) => !open)}
      >
        {isOpen ? <CloseIcon /> : (launcherIcon ?? <ChatIcon />)}
      </button>
    </div>
  );
}

function ChatIcon(): ReactElement {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M4 6a3 3 0 0 1 3-3h10a3 3 0 0 1 3 3v7a3 3 0 0 1-3 3H9.4L5.7 19.7A1 1 0 0 1 4 19v-3z"
        fill="currentColor"
      />
    </svg>
  );
}

function CloseIcon(): ReactElement {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M6 6l12 12M18 6L6 18"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

function PaperclipIcon(): ReactElement {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M20.5 11.5l-8.2 8.2a5 5 0 0 1-7.1-7.1l8.5-8.5a3.3 3.3 0 0 1 4.7 4.7l-8.5 8.5a1.7 1.7 0 0 1-2.4-2.4l7.8-7.8"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SendIcon(): ReactElement {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4 12L20 4l-4.5 16-4-6.5z" fill="currentColor" />
    </svg>
  );
}

export default ChatWidget;
